import api from '../api';
import type { InventoryLot, StockMovement } from '../../types/inventory';
import { mapDtoToInventoryLot, mapDtoToStockMovement } from './inventoryMappers';
import { cache } from '../../utils/cache';

export const inventoryLotsService = {
  getLots: async (itemId: string): Promise<InventoryLot[]> => {
    try {
      const response = await api.get(`/inventory/${itemId}/lots`);
      const data = response.data.data || response.data || [];
      return Array.isArray(data) ? data.map(mapDtoToInventoryLot) : [];
    } catch (error) {
      console.error('Error fetching lots:', error);
      return [];
    }
  },

  getAllLots: async (
    params: {
      status?: string;
      item_id?: string;
      item_type?: string;
      search?: string;
      start_date?: string;
      end_date?: string;
      order_by?: string;
      order_dir?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<{ data: InventoryLot[]; total: number; totalRemainingQuantity?: number }> => {
    // Generate a unique cache key based on params if needed, 
    // but for "cache management per page", we might want a simpler cache or just rely on React Query.
    // However, following the pattern in PurchaseOrderService:
    const cacheKey = `inventory_lots_all_${JSON.stringify(params)}`;
    return cache.fetch(cacheKey, async () => {
      const response = await api.get('/inventory/lots', { params });
      return {
        data: (response.data.data || []).map(mapDtoToInventoryLot),
        total: response.data.meta.total,
        totalRemainingQuantity: response.data.meta.total_remaining_quantity,
      };
    });
  },

  getLotById: async (id: string): Promise<InventoryLot> => {
    const response = await api.get(`/inventory/lots/${id}`);
    return mapDtoToInventoryLot(response.data);
  },

  getLotHistory: async (id: string, page = 1, limit = 20): Promise<{ data: StockMovement[]; total: number }> => {
    const offset = (page - 1) * limit;
    const response = await api.get(`/inventory/lots/${id}/history`, {
      params: { limit, offset },
    });
    return {
      data: (response.data.data || []).map(mapDtoToStockMovement),
      total: response.data.meta.total,
    };
  },

  uploadImage: async (lotId: string, file: File): Promise<{ path: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/uploads/inventory/${lotId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteImage: async (lotId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/uploads/inventory/${lotId}/image`);
    return response.data;
  },
};
